<template>
  <b-container fluid>
    <b-row>
      <b-col
        v-show="
          isEmptyObj(user) || (!recentReqFilesLoading && !requests.length)
        "
        cols="12"
        class="py-5 text-center"
      >
        <h3 class="font-weight-bold">This lot is vacant</h3>
        <p class="text-secondary">You have no files yet.</p>
        <b-img fluid src="/img/empty-req.svg" alt="Empty File Requests"></b-img>
      </b-col>
      <b-col
        v-if="!isEmptyObj(user) && (recentReqFilesLoading || requests.length)"
        cols="12"
        class="p-0 mb-0"
      >
        <!-- Mobile: compact list rows (hidden on md+) -->
        <div class="d-md-none mobile-request-list">
          <div
            v-if="recentReqFilesLoading"
            class="text-center text-primary my-4"
          >
            <digger-loader />
          </div>
          <div v-else>
            <div
              v-for="item in requests"
              :key="item.id"
              class="mobile-request-item c-pointer"
              @click="viewRequestDetails(item)"
            >
              <div class="d-flex align-items-start justify-content-between">
                <span class="mobile-request-item__name">
                  {{
                    item.file_share && item.file_share.file
                      ? item.file_share.file.name
                      : item.file_request.name
                  }}
                </span>
                <b-badge
                  pill
                  :variant="
                    statusBadge(
                      item.file_share && item.file_share.file
                        ? item.file_share.file.status
                        : '',
                    )
                  "
                  class="badge-hovered text-capitalize ml-2 flex-shrink-0"
                >
                  {{
                    item.file_share && item.file_share.file
                      ? item.file_share.file.status
                      : 'Not Started'
                  }}
                </b-badge>
              </div>
              <div class="mobile-request-item__meta">
                {{ mobileAssignedName(item) }}
                <span class="mx-1">·</span>
                {{ item.file_request.due_date | formatDate }}
                <template v-if="item.file_request.renewing_file_name">
                  <span class="mx-1">·</span>
                  Renewal for {{ item.file_request.renewing_file_name }}
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            responsive
            hover
            show-empty
            ref="recentReqFilesTable"
            :fields="fields"
            :items="requests"
            :busy="recentReqFilesLoading"
            @row-clicked="viewRequestDetails"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <digger-loader />
              </div>
            </template>

            <template #head()="data">
              <span class="text-capitalize">{{ data.label }}</span>
            </template>

            <template #empty>
              <h6 class="text-center">Empty request file/s data</h6>
            </template>

            <template #emptyfiltered>
              <h6 class="text-center">No request file/s data found</h6>
            </template>

            <template
              #cell(name)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{
                data.item.file_share && data.item.file_share.file
                  ? data.item.file_share.file.name
                  : data.item.file_request.name
              }}
            </template>

            <template
              #cell(file_request.renewing_file_name)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              <!-- Only a request created via "Request Renewal" carries a
                   renewing_file_name — a fresh, no-prior-file request has
                   nothing to link back to. -->
              {{ data.item.file_request.renewing_file_name || '-' }}
            </template>

            <template
              #cell(file_request.due_date)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{ data.item.file_request.due_date | formatDate }}
            </template>

            <template
              #cell(user.full_name)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{ data.item.user.full_name || data.item.user.email }}
            </template>

            <template
              #cell(file_request.user.full_name)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{
                data.item.file_request &&
                data.item.file_request.user &&
                data.item.file_request.user.full_name
                  ? data.item.file_request.user.full_name
                  : data.item.file_request &&
                    data.item.file_request.user &&
                    data.item.file_request.user.email
                  ? data.item.file_request.user.email
                  : '-'
              }}
            </template>

            <template
              #cell(file_share.file.status)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              <template
                v-if="data.item.file_share && data.item.file_share.file"
              >
                <b-badge
                  pill
                  :variant="statusBadge(data.item.file_share.file.status)"
                  class="badge-hovered text-capitalize"
                >
                  {{ data.item.file_share.file.status }}
                </b-badge>
              </template>
              <template v-else>
                <b-badge
                  pill
                  variant="secondary"
                  class="badge-hovered text-capitalize"
                >
                  Not Started
                </b-badge>
              </template>
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>
    <b-pagination
      v-if="rows > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :current-page="currentPage"
      aria-controls="my-table"
      align="center"
      size="sm"
    />
    <b-modal
      centered
      v-model="delModalShow"
      id="delete-file-modal"
      title="Delete selected file?"
      modal-class="confirm-modal"
    >
      <p class="my-4">Are you sure you want to delete the selected file?</p>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelDelFiles()"> No </b-button>
        <b-button variant="primary" @click="deleteFiles(delModalId)">
          Yes
        </b-button>
      </template>
    </b-modal>

    <b-modal
      centered
      v-model="notStartedModalShow"
      id="not-started-file-modal"
      title="Choose existing file"
      modal-class="confirm-modal"
      @hide="notStartedHide"
    >
      <p v-if="request" style="opacity: 1; line-height: 28px">
        <b>Requested By: </b>
        {{ request.user.full_name }} from {{ request.user.company }}<br />
        <b>Requested File: </b>{{ request.name }}<br />
        <b>Due Date: </b>{{ request.due_date_formatted }}<br />
        <b>Instruction: </b>{{ request.instruction }}<br />
      </p>

      <p class="my-4">
        Would you like to browse and choose from your existing files?
      </p>

      <template #modal-footer>
        <b-button variant="secondary" @click="goToNewFile">
          No, create new file
        </b-button>
        <b-button
          variant="primary"
          @click="
            $router.replace({
              name: 'request.files-page',
              query: {
                name: request ? request.name : undefined,
                template_id:
                  request && request.template ? request.template.id : undefined,
              },
            })
          "
        >
          Yes, browse files
        </b-button>
      </template>
    </b-modal>

    <b-alert
      dismissible
      fade
      class="custom-alert"
      :show="dismissCountDown"
      :variant="alertType"
      @dismissed="clearAlert"
      @dismiss-count-down="dlCountDownChanged"
    >
      {{ alertMsg }}
    </b-alert>
  </b-container>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import { isEmpty } from 'lodash'
import dayjs from 'dayjs'

export default {
  data() {
    return {
      recentReqFilesLoading: false,
      dismissCountDown: 0,
      alertMsg: null,
      alertType: 'primary',
      delModalShow: false,
      delModalId: 0,
      notStartedModalShow: false,
      selectedRequestId: null,
      request: null,
      perPage: 10,
      currentPage: 1,
      activeRequestFilters: null,
    }
  },

  computed: {
    ...mapGetters('requests', ['requests', 'requestsMeta']),
    ...mapGetters('auth', ['user']),

    userHeaderKey() {
      if (!isEmpty(this.user)) {
        if (this.user.user_type === 'tradie') {
          return 'file_request.user.full_name'
        } else {
          return 'user.full_name'
        }
      } else {
        return ''
      }
    },

    userHeaderLabel() {
      if (!isEmpty(this.user)) {
        if (this.user.user_type === 'tradie') {
          return 'Assigned By'
        } else {
          return 'Assigned To'
        }
      } else {
        return ''
      }
    },

    fields() {
      const baseFields = [
        {
          key: 'name',
          label: 'File Name',
          sortable: true,
        },
        {
          key: 'file_request.due_date',
          label: 'Due Date',
          sortable: true,
        },
        // {
        //   key: 'file_request.project',
        //   label: 'Company Requesting',
        //   sortable: true,
        // },
        {
          key: this.userHeaderKey,
          label: this.userHeaderLabel,
          sortable: true,
        },
      ]

      if (!isEmpty(this.user) && this.user.user_type === 'business_admin') {
        baseFields.push({
          key: 'file_request.user.full_name',
          label: 'Created By',
          sortable: true,
        })
      }

      baseFields.push({
        key: 'file_share.file.status',
        label: 'Status',
        sortable: true,
      })

      return baseFields
    },
    rows() {
      return this.requestsMeta ? this.requestsMeta.total : 0
    },
  },

  filters: {
    formatDate(date) {
      return dayjs(date).format('DD MMM YYYY')
    },
  },

  methods: {
    ...mapActions('requests', {
      getRequestedTo: 'getRequestedTo',
      getRequestedBy: 'getRequestedBy',
    }),
    ...mapMutations('requests', {
      setFileRequest: 'setFileRequest',
      clearFileRequest: 'clearFileRequest',
    }),

    async getRecentRequestedFiles(page = 1, limit = 10, filter = null) {
      this.recentReqFilesLoading = true

      if (!isEmpty(this.user)) {
        if (this.user.user_type === 'tradie') {
          await this.getRequestedTo({ page, limit, filters: filter }).finally(
            () => {
              this.recentReqFilesLoading = false
            },
          )
        }

        if (this.user.user_type === 'business_admin') {
          await this.getRequestedBy({ page, limit, filters: filter }).finally(
            () => {
              this.recentReqFilesLoading = false
            },
          )
        }
      }
    },

    viewRequestDetails(item, i) {
      if (this.user.user_type === 'tradie') {
        if (
          !(
            item.file_share &&
            item.file_share.file &&
            (item.file_share.file.status !== 'valid' ||
              item.file_share.file.status !== 'rejected' ||
              item.file_share.file.status !== 'pending approval')
          )
        ) {
          this.setFileRequest(item.file_request_id)
          this.notStartedShow(item.id)
        } else {
          this.$router
            .push({
              name: 'request.view-requested-by-page',
              params: {
                id: item.id,
              },
            })
            .catch(() => {})
        }
      }

      if (this.user.user_type === 'business_admin') {
        this.$router
          .push({
            name: 'request.view-requested-to-page',
            params: {
              id: item.file_request_id,
            },
          })
          .catch(() => {})
      }
    },

    async deleteFiles(id) {
      await this.$api
        .delete(`file/${id}`)
        .then(async ({ data }) => {
          if (data.success) {
            this.alertMsg = 'Delete file successful!'
            this.dismissCountDown = 5
            await this.getRecentRequestedFiles(
              this.currentPage,
              this.perPage,
              this.activeRequestFilters,
            )
            this.cancelDelFiles()
          }
        })
        .catch((e) => {
          setTimeout((e) => {
            this.clearAlert()
          }, 5000)

          this.alertType = 'danger'
          this.alertMsg = 'Something went wrong while deleting file!'
          this.dismissCountDown = 5
          this.cancelDelFiles()
        })
    },

    showDelFiles(id) {
      this.delModalShow = true
      this.delModalId = id
    },

    cancelDelFiles() {
      this.delModalShow = false
      this.delModalId = 0
    },

    dlCountDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    clearAlert() {
      this.dismissCountDown = 0
      this.alertMsg = null
      this.alertType = 'primary'
    },

    isEmptyObj(obj) {
      return isEmpty(obj)
    },

    goToNewFile() {
      const templateId =
        this.request && this.request.template && this.request.template.id
      if (templateId) {
        this.$router.replace({
          name: 'request.request-file-template-form-page',
          params: {
            fileRequestUserId: this.selectedRequestId,
            id: String(templateId),
          },
        })
      } else {
        this.$router.replace({
          name: 'request.request-file-templates-page',
          params: {
            fileRequestUserId: this.selectedRequestId,
          },
        })
      }
    },

    notStartedShow(selectedRequestId) {
      this.request = this.requests.find(
        (x) => x.id === selectedRequestId,
      ).file_request

      const date = new Date(this.request.due_date)
      this.request.due_date_formatted = `
        ${date.getDate().toString().padStart(2, '0')}/
        ${(date.getMonth() + 1).toString().padStart(2, '0')}/
        ${date.getFullYear()}`

      this.notStartedModalShow = true
      this.selectedRequestId = selectedRequestId
    },

    notStartedHide() {
      this.notStartedModalShow = false
      this.selectedRequestId = null
      this.clearFileRequest()
    },

    mobileAssignedName(item) {
      if (this.user && this.user.user_type === 'tradie') {
        return (
          (item.file_request &&
            item.file_request.user &&
            (item.file_request.user.full_name ||
              item.file_request.user.email)) ||
          '-'
        )
      }
      return (item.user && (item.user.full_name || item.user.email)) || '-'
    },

    statusBadge(status) {
      return status === 'valid'
        ? 'success'
        : status === 'pending approval'
        ? 'warning'
        : status === 'rejected'
        ? 'danger'
        : 'secondary'
    },
  },

  watch: {
    currentPage: async function () {
      await this.getRecentRequestedFiles(
        this.currentPage,
        this.perPage,
        this.activeRequestFilters,
      )
    },

    recentReqFilesLoading(newVal, oldVal) {
      this.$emit('loading', newVal)
    },
  },

  async mounted() {
    const normalizeFilters = (raw) => {
      if (!raw || typeof raw !== 'object') {
        return { ownedBy: '', search: '', status: [] }
      }
      const ownedBy =
        raw.ownedBy != null && String(raw.ownedBy)
          ? String(raw.ownedBy).trim()
          : ''
      const search =
        raw.search != null && String(raw.search)
          ? String(raw.search).trim()
          : ''
      const status = Array.isArray(raw.status) ? raw.status : []
      return { ownedBy, search, status }
    }

    const applyFilters = async (raw) => {
      const filters = normalizeFilters(raw)
      this.activeRequestFilters = filters
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.getRecentRequestedFiles(
          1,
          this.perPage,
          this.activeRequestFilters,
        )
      }
    }

    this.queueRequestFilters = debounce(applyFilters, 400)

    this.onSubmitRequestsFilters = (payload) => {
      if (!payload || typeof payload !== 'object') return
      const immediate = payload.__immediate === true
      if (immediate) {
        this.queueRequestFilters.cancel()
        applyFilters(payload)
      } else {
        this.queueRequestFilters(payload)
      }
    }

    this.$root.$on('submitRequestsFilters', this.onSubmitRequestsFilters)

    if (!isEmpty(this.user)) {
      await this.getRecentRequestedFiles(
        1,
        this.perPage,
        this.user.user_type === 'business_admin'
          ? this.activeRequestFilters
          : null,
      )
    }
  },

  beforeDestroy() {
    if (this.queueRequestFilters) {
      this.queueRequestFilters.cancel()
    }
    if (this.onSubmitRequestsFilters) {
      this.$root.$off('submitRequestsFilters', this.onSubmitRequestsFilters)
    }
  },
}
</script>

<style lang="scss" scoped>
// The wrapping column's own bottom margin is zeroed (to kill the global
// .col-12 30px gotcha, which stacked with the table's margin into a much
// bigger gap than intended) — restore a small gap here before the
// pagination on phones, where this stacked-card list replaces the table.
.mobile-request-list {
  padding-bottom: 1rem;
}

.mobile-request-item {
  display: flex;
  flex-direction: column;
  padding: 0.7rem 1.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;

  &__name {
    font-weight: 600;
    font-size: 0.875rem;
    color: #364052;
    line-height: 1.3;
  }

  &__meta {
    font-size: 0.75rem;
    color: #8b95a3;
    margin-top: 0.25rem;
  }
}

/deep/ .b-table {
  & > tbody {
    & > tr:not(:hover) {
      & .show-when-hovered {
        display: none;
      }
    }

    & > tr:hover {
      & .show-when-hovered {
        display: flex;
      }
    }
  }
}

/deep/ .b-table tr {
  cursor: pointer;
}

/deep/ .b-table {
  & > thead > tr,
  & > tbody > tr {
    & > th:first-child,
    & > td:first-child {
      padding-left: 1.5rem !important;
    }

    & > th:last-child,
    & > td:last-child {
      padding-right: 1.5rem !important;
    }
  }
}

/deep/ .recent-req-file-action-col {
  width: 3vw !important;
}

@media (pointer: fine) {
  /deep/ .b-table {
    & > tbody {
      & > tr:not(:hover) {
        & .show-when-hovered {
          display: none;
          // visibility: hidden;
        }
      }

      & > tr:hover {
        & .show-when-hovered {
          display: inherit;
          // visibility: visible;
        }
      }
    }
  }
}
</style>
